import { z } from 'zod';

/**
 * Payment — append-only movement ledger. Signed `amountCents` lets us
 * represent refunds/corrections as inverse rows without ever mutating
 * a past payment. A payment settles exactly one source (PO or medição)
 * in MVP; split-settlement moves to a join table in a future wave.
 */
export const paymentKindSchema = z.enum([
  'po',
  'measurement',
  'reimbursement',
  'refund',
  'other',
]);
export type PaymentKind = z.infer<typeof paymentKindSchema>;

export const paymentMethodSchema = z.enum([
  'pix',
  'boleto',
  'transfer',
  'cash',
  'other',
]);
export type PaymentMethod = z.infer<typeof paymentMethodSchema>;

export const paymentStatusSchema = z.enum([
  'pending',
  'settled',
  'failed',
  'cancelled',
]);
export type PaymentStatus = z.infer<typeof paymentStatusSchema>;

export const paymentSchema = z.object({
  id: z.string(),
  projectId: z.string(),
  kind: paymentKindSchema,
  purchaseOrderId: z.string().nullable(),
  measurementId: z.string().nullable(),
  supplierId: z.string().nullable(),
  supplierName: z.string().nullable(),
  amountCents: z.number().int(),
  /**
   * Portion of amountCents withheld as retention (caução) against the
   * linked PO. Net cash moved = amountCents - holdbackCents. For release
   * payments this is 0 — the amount itself IS the release.
   */
  holdbackCents: z.number().int().min(0),
  /**
   * When non-null, this payment row is a retention *release* against the
   * named PO. In that case `kind` is 'other', `holdbackCents` is 0, and
   * `amountCents` is the outflow paying down the held balance.
   */
  releaseOfPoId: z.string().nullable(),
  method: paymentMethodSchema,
  pixTxId: z.string().nullable(),
  reference: z.string().nullable(),
  status: paymentStatusSchema,
  occurredAt: z.string(),
  settledAt: z.string().nullable(),
  notes: z.string().nullable(),
  recordedBy: z.string(),
  recordedByName: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Payment = z.infer<typeof paymentSchema>;

export const createPaymentRequestSchema = z
  .object({
    kind: paymentKindSchema,
    purchaseOrderId: z.string().optional(),
    measurementId: z.string().optional(),
    supplierId: z.string().optional(),
    amountCents: z.number().int(),
    /**
     * Optional explicit override for the retention portion of this
     * payment. When omitted and the linked PO has retention_pct_bp > 0,
     * the server auto-computes it as round(amountCents * pct/10000).
     * Set to 0 explicitly to bypass auto-withhold on an otherwise-
     * retentioned PO (rare — e.g. a mobilization advance).
     */
    holdbackCents: z.number().int().min(0).optional(),
    method: paymentMethodSchema,
    pixTxId: z.string().trim().max(120).optional(),
    reference: z.string().trim().max(200).optional(),
    occurredAt: z.string().datetime({ offset: true }).optional(),
    notes: z.string().trim().max(2000).optional(),
  })
  .refine(
    (v) => {
      // Source-of-truth: every payment must link to *something* so we can
      // reconcile. Exception: `other` accepts free-form reference strings.
      if (v.kind === 'po') return !!v.purchaseOrderId;
      if (v.kind === 'measurement') return !!v.measurementId;
      return true;
    },
    { message: 'Missing linked purchaseOrderId/measurementId for kind' },
  )
  .refine((v) => (v.holdbackCents ?? 0) <= v.amountCents, {
    message: 'holdbackCents cannot exceed amountCents',
  });
export type CreatePaymentRequest = z.infer<typeof createPaymentRequestSchema>;

export const settlePaymentRequestSchema = z.object({
  settledAt: z.string().datetime({ offset: true }).optional(),
  pixTxId: z.string().trim().max(120).optional(),
});
export type SettlePaymentRequest = z.infer<typeof settlePaymentRequestSchema>;
